import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { MEMBERS_DATA_SERVICE, MembersDataService } from '@app/app/page-modules/admin/services/members/members-data.service';
import { ROLES_DATA_SERVICE, RolesDataService } from '@app/app/page-modules/admin/services/roles/roles-data.service';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { SnackbarHelper } from '@app/app/shared/helpers/snackbar-helper';
import { Organization, OrganizationRoles } from '@app/app/shared/models';
import { MemberInvitationRequest } from '@app/app/shared/models/admin/members.model';
import { SelectOption } from '@app/app/shared/styleguide/components/selects/sg-select/sg-select.component';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { LucideTriangleAlert, LucideX } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'ptl-v3-invite-member',
  templateUrl: './v3-invite-member.component.html',
  styleUrls: ['./v3-invite-member.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3InviteMemberComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();
  @Output() invitationSent = new EventEmitter<void>();

  emailControl = new FormControl<string>('', [Validators.required, Validators.email]);
  static readonly DEFAULT_ROLE = '__LEARNER_DEFAULT__';

  roleControl = new FormControl<string>(V3InviteMemberComponent.DEFAULT_ROLE);

  roleOptions: SelectOption<string>[] = [];
  invitationInProgress = false;
  showEmailError = false;

  protected readonly LucideX = LucideX;
  protected readonly LucideTriangleAlert = LucideTriangleAlert;

  @Select(UserAuthState.organizationDetails)
  private organization$: Observable<Organization>;

  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);
  private translocoService = inject(TranslocoService);
  private membersDataService = inject<MembersDataService>(MEMBERS_DATA_SERVICE);
  private rolesDataService = inject<RolesDataService>(ROLES_DATA_SERVICE);

  ngOnInit(): void {
    this.roleOptions = [
      {
        value: V3InviteMemberComponent.DEFAULT_ROLE,
        label: this.translocoService.translate('translations.invitations.quick.role.learner'),
      },
    ];
    this.roleControl.setValue(V3InviteMemberComponent.DEFAULT_ROLE);

    this.organization$.pipe(takeUntil(this.destroy$)).subscribe((organization) => {
      if (!organization?._id) {
        return;
      }
      this.rolesDataService.loadOrganizationRoles(organization._id).subscribe(({ isSuccess, value }) => {
        if (isSuccess && value?.length) {
          const roles: SelectOption<string>[] = value.map((role: OrganizationRoles) => ({
            value: role._id,
            label: LanguageCodeHelper.getDataByLanguageCode(role.name)?.value || role.type,
          }));
          this.roleOptions = [...this.roleOptions, ...roles];
          this.cdr.markForCheck();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.closed.emit();
  }

  onCancel(): void {
    this.emailControl.reset('');
    this.roleControl.setValue(V3InviteMemberComponent.DEFAULT_ROLE);
    this.showEmailError = false;
    this.closed.emit();
  }

  onSendInvite(): void {
    if (this.invitationInProgress) {
      return;
    }
    if (this.emailControl.invalid) {
      this.emailControl.markAsTouched();
      this.showEmailError = true;
      return;
    }
    const email = (this.emailControl.value || '').trim();
    if (!email) {
      return;
    }

    const roleId = this.roleControl.value;
    const isRealRole = !!roleId && roleId !== V3InviteMemberComponent.DEFAULT_ROLE;
    const request: MemberInvitationRequest = {
      emails: [email],
      groupsToAdd: [],
      playlistsToSubscribe: [],
      rolesToAssign: isRealRole ? [roleId] : [],
      affiliationSelectionEnabled: true,
    };

    this.invitationInProgress = true;
    this.cdr.markForCheck();

    this.membersDataService.inviteNewMembers(request).subscribe(({ isSuccess, value, error }) => {
      this.invitationInProgress = false;
      if (isSuccess) {
        const notSent = value?.emailsNotSent ?? [];
        const sent = value?.emailsSent ?? [];
        const resent = value?.emailsReSent ?? [];

        let message = this.translocoService.translate('translations.invitations.message.success.send');
        if (notSent.length && !resent.length && !sent.length) {
          message = this.translocoService.translate('translations.invitations.message.error.send').replace('{emails}', notSent.join(', '));
        } else if (notSent.length && (resent.length || sent.length)) {
          message = this.translocoService
            .translate('translations.invitations.message.error.sendSomeEmails')
            .replace('{emails}', notSent.join(', '));
        }
        SnackbarHelper.showSnackBarV3(this.ngZone, this.snackBar, message, 5000);

        if (sent.length || resent.length) {
          this.invitationSent.emit();
          this.emailControl.reset('');
          this.roleControl.setValue(V3InviteMemberComponent.DEFAULT_ROLE);
          this.closed.emit();
        }
      } else {
        SnackbarHelper.showSnackBarV3(this.ngZone, this.snackBar, error || '', 5000);
      }
      this.cdr.markForCheck();
    });
  }

  onOpenMemberInvites(): void {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/admin/invitations');
  }
}
